import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, Col, Container, Row, Table } from 'reactstrap';
import { Breadcrumbs } from '../../components/Common';
import Spinner from '../../components/Common/Spinner';
import useAuthUser from '../../common/hooks/authUser';
import { isDealerPortalNavFromInventory } from '../../common/dealerPortalNavigation';
import type { TDealerPortalNavState } from '../../common/dealerPortalNavigation';
import { toastError } from '../../helpers/toast-helper';
import { getFormattedCurrency } from '../../helpers/currency-helper';
import { getTableFormattedDate } from '../../helpers/date-time-helper';
import { normalizeCustomerNumberForLookup } from '../../helpers/dealer-customer-input';
import { getDealerPortalOrderDetail } from '../../api/dealerPortal';
import type { TSyteLineOrder } from '../../api/dealerPortal/types';

function formatOrderDate(value: unknown): string {
	if (value == null || value === '') return '—';
	const s = typeof value === 'string' ? value : String(value);
	return getTableFormattedDate(s) || '—';
}

const DealerOrderDetailsPage = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const redirectDoneRef = useRef(false);
	const { orderNumber: orderNumberParam } = useParams<{ orderNumber: string }>();
	const [searchParams] = useSearchParams();
	const customerNumber = useMemo(
		() => normalizeCustomerNumberForLookup(searchParams.get('customerNumber') ?? ''),
		[searchParams],
	);
	const orderNumber = orderNumberParam ?? '';

	const { user, isAdmin, isDataLoading } = useAuthUser();
	const [order, setOrder] = useState<TSyteLineOrder | null>(null);
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		document.title = 'Order details';
	}, []);

	useEffect(() => {
		if (user && !isAdmin) {
			toastError('Access denied. Admin privileges required.');
			navigate('/');
		}
	}, [user, isAdmin, navigate]);

	useEffect(() => {
		if (!user || !isAdmin) return;
		const allowed = isDealerPortalNavFromInventory(location.state);
		if (!allowed || !customerNumber || !orderNumber) {
			if (!redirectDoneRef.current) {
				redirectDoneRef.current = true;
				toastError('Open order details from Dealer inventory after selecting a dealer.');
				navigate('/admin/dealer-inventory', { replace: true });
			}
			setIsLoading(false);
			return;
		}
	}, [user, isAdmin, location.state, customerNumber, orderNumber, navigate]);

	useEffect(() => {
		if (!isDealerPortalNavFromInventory(location.state)) {
			setIsLoading(false);
			return;
		}
		if (!orderNumber || !customerNumber) {
			setIsLoading(false);
			setOrder(null);
			return;
		}
		let cancelled = false;
		(async () => {
			setIsLoading(true);
			try {
				const data = await getDealerPortalOrderDetail(orderNumber, customerNumber);
				if (cancelled) return;
				if (data.errorMessage) {
					toastError(data.errorMessage);
					setOrder(null);
				} else {
					setOrder(data);
				}
			} catch (e) {
				if (!cancelled) {
					toastError(e instanceof Error ? e.message : 'Failed to load order.');
					setOrder(null);
				}
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [orderNumber, customerNumber, location.state]);

	if (isDataLoading) {
		return <Spinner />;
	}

	if (user && !isAdmin) {
		return null;
	}

	if (!isDealerPortalNavFromInventory(location.state) || !customerNumber || !orderNumber) {
		return null;
	}

	const navState = { fromDealerInventory: true } satisfies TDealerPortalNavState;
	const backHref = `/admin/orders?${new URLSearchParams({ customerNumber }).toString()}`;
	const lines = order?.orderLines ?? [];

	return (
		<Container fluid>
			<Breadcrumbs title="Order details" />
			<Row className="mb-3">
				<Col>
					<Button tag={Link} to={backHref} state={navState} color="light" className="mb-2">
						← Back to orders
					</Button>
				</Col>
			</Row>

			{!orderNumber || !customerNumber ? (
				<p className="text-muted">Missing order or customer. Use a view link from the orders list.</p>
			) : isLoading ? (
				<Spinner />
			) : !order ? (
				<p className="text-muted">Order could not be loaded.</p>
			) : (
				<>
					<Row className="mb-3">
						<Col md={6}>
							<dl className="row mb-0 small">
								<dt className="col-sm-4">Order #</dt>
								<dd className="col-sm-8">{order.orderNumber ?? '—'}</dd>
								<dt className="col-sm-4">Customer</dt>
								<dd className="col-sm-8">
									{order.customerName ?? '—'}{' '}
									<span className="text-muted">({order.customerNumber ?? '—'})</span>
								</dd>
								<dt className="col-sm-4">Order date</dt>
								<dd className="col-sm-8">{formatOrderDate(order.orderDate)}</dd>
								<dt className="col-sm-4">Customer PO</dt>
								<dd className="col-sm-8">{order.customerPO ?? '—'}</dd>
								<dt className="col-sm-4">Total amount</dt>
								<dd className="col-sm-8">{getFormattedCurrency(order.orderCost)}</dd>
								<dt className="col-sm-4">Last projected completion</dt>
								<dd className="col-sm-8">{formatOrderDate(order.lastProjectedCompletionDate)}</dd>
							</dl>
						</Col>
					</Row>

					<h5 className="mb-2">Lines</h5>
					<div className="table-responsive">
						<Table bordered size="sm" className="align-middle">
							<thead className="table-light">
								<tr>
									<th>#</th>
									<th>Item</th>
									<th>Description</th>
									<th>Serial</th>
									<th className="text-end">Qty</th>
									<th>Projected completion</th>
									<th className="text-end">List</th>
									<th className="text-end">Discount</th>
									<th className="text-end">Net</th>
								</tr>
							</thead>
							<tbody>
								{lines.length === 0 && (
									<tr>
										<td colSpan={9} className="text-muted">
											No line items
										</td>
									</tr>
								)}
								{lines.map((line, lineIdx) => (
									<tr key={`${line.lineNumber}-${lineIdx}`}>
										<td>{line.lineNumber}</td>
										<td>{line.item ?? '—'}</td>
										<td>{line.description ?? '—'}</td>
										<td>{line.serialNumber ?? '—'}</td>
										<td className="text-end">{line.quantity?.toLocaleString() ?? '—'}</td>
										<td>{formatOrderDate(line.projectedCompletionDate)}</td>
										<td className="text-end">{getFormattedCurrency(line.listPrice)}</td>
										<td className="text-end">{getFormattedCurrency(line.discount)}</td>
										<td className="text-end">{getFormattedCurrency(line.netPrice)}</td>
									</tr>
								))}
							</tbody>
						</Table>
					</div>
				</>
			)}
		</Container>
	);
};

export default DealerOrderDetailsPage;
